import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { useConfirm } from '../components/ConfirmDialog';
import { Card, SectionTitle, Field, Spinner, ErrorNote, Empty, SelectMenu } from '../components/ui';
import { fmtDateTime } from '../lib/format';

const MAX_LOGO_KB = 500;

const MIN_PASSWORD = 8;

/**
 * The backend route (POST /api/users/change-password) existed with no way to
 * reach it — the only path to a new password was the forgot-password email
 * flow. Fine for a first login, not for routine use.
 */
function ChangePasswordSection() {
  const { toast, run } = useApp();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (next.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    if (next !== confirm) return setError('The two new passwords don’t match.');

    run(async () => {
      setBusy(true);
      await api.post('/users/change-password', { currentPassword: current, newPassword: next });
      setCurrent(''); setNext(''); setConfirm('');
      toast('Password changed');
      setBusy(false);
    }, { onError: (m) => { setBusy(false); setError(m); } });
  };

  return (
    <Card>
      <SectionTitle>Change password</SectionTitle>
      <form className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }} onSubmit={submit}>
        <Field label="Current password" id="curpw">
          <input id="curpw" className="fi" type="password" autoComplete="current-password" required
            value={current} onChange={e => setCurrent(e.target.value)} />
        </Field>
        <div className="row-fields">
          <Field label="New password" id="newpw" hint={`At least ${MIN_PASSWORD} characters`}>
            <input id="newpw" className="fi" type="password" autoComplete="new-password" required
              value={next} onChange={e => setNext(e.target.value)} />
          </Field>
          <Field label="Confirm new password" id="confirmpw" error={error}>
            <input id="confirmpw" className="fi" type="password" autoComplete="new-password" required
              value={confirm} onChange={e => setConfirm(e.target.value)} />
          </Field>
        </div>
        <button className="btn" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</button>
      </form>
    </Card>
  );
}

function CrewSection() {
  const { user, toast, run } = useApp();
  const { data: crew, loading, reload } = useAsync(
    () => (user?.role === 'owner' ? api.get('/users') : Promise.resolve(null)), [user?.role],
  );
  const [invite, setInvite] = useState({ name: '', email: '', role: 'rep' });
  const [busy, setBusy] = useState(false);

  if (user?.role !== 'owner') return null;

  const sendInvite = (e) => {
    e.preventDefault();
    run(async () => {
      setBusy(true);
      await api.post('/users/invite', invite);
      setInvite({ name: '', email: '', role: 'rep' });
      toast(`Invite sent to ${invite.email}`);
      reload();
      setBusy(false);
    }, { onError: (m) => { setBusy(false); toast(m); } });
  };

  const setStatus = (u, status) => run(async () => {
    await api.patch(`/users/${u.id}`, { status });
    toast(status === 'disabled' ? 'Access revoked' : 'Access restored');
    reload();
  });

  return (
    <Card>
      <SectionTitle sub="Each person gets their own login. Invite by email; they choose their own password.">
        Crew
      </SectionTitle>

      {loading ? <Spinner label="Loading crew" /> : null}

      <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
        {(crew || []).map(u => (
          <div className="row between" key={u.id} style={{ padding: 'var(--s-2) 0', borderBottom: '1px solid var(--border-soft)' }}>
            <div style={{ minWidth: 0 }}>
              <div className="nm">{u.name} {u.id === user.id ? <span className="sb">(you)</span> : null}</div>
              <div className="sb">{u.email} · {u.role}{u.status !== 'active' ? ` · ${u.status}` : ''}</div>
            </div>
            {u.id !== user.id ? (
              <button className="btn sm" onClick={() => setStatus(u, u.status === 'disabled' ? 'active' : 'disabled')}>
                {u.status === 'disabled' ? 'Restore' : 'Revoke'}
              </button>
            ) : null}
          </div>
        ))}
        {crew && !crew.length ? <Empty>No crew yet.</Empty> : null}
      </div>

      <form className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-4)' }} onSubmit={sendInvite}>
        <div className="row-fields">
          <Field label="Name" id="invname">
            <input id="invname" className="fi" required value={invite.name}
              onChange={e => setInvite(i => ({ ...i, name: e.target.value }))} />
          </Field>
          <Field label="Email" id="invemail">
            <input id="invemail" className="fi" type="email" required value={invite.email}
              onChange={e => setInvite(i => ({ ...i, email: e.target.value }))} />
          </Field>
        </div>
        <Field label="Role" id="invrole">
          <SelectMenu
            id="invrole"
            value={invite.role}
            onChange={role => setInvite(i => ({ ...i, role }))}
            options={[
              { value: 'rep', label: 'Field rep' },
              { value: 'owner', label: 'Owner' },
            ]}
          />
        </Field>
        <button className="btn" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
      </form>
    </Card>
  );
}

export default function Settings() {
  const { setSettings, signOut, toast, run } = useApp();
  const logoRef = useRef();
  const [ask, confirmDialog] = useConfirm();

  const { data, error, loading, reload } = useAsync(() => Promise.all([
    api.get('/settings'),
    api.get('/email-log').catch(() => ({ status: { mode: 'demo' }, entries: [] })),
  ]), []);

  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF({ ...data[0] }); }, [data]);

  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  if (loading || !f) return <Spinner label="Loading settings" />;
  const [, emailLog] = data;

  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const setNum = (k) => (e) => setF(p => ({ ...p, [k]: Number(e.target.value) }));

  const save = () => run(async () => {
    setBusy(true);
    const saved = await api.put('/settings', f);
    setSettings(saved);
    toast('Saved');
    setBusy(false);
  }, { onError: (m) => { setBusy(false); toast(m); } });

  function onLogo(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_KB * 1024) return toast(`Logo must be under ${MAX_LOGO_KB}KB`);
    const fr = new FileReader();
    fr.onload = () => run(async () => {
      await api.post('/brand/logo', { dataUrl: fr.result });
      toast('Logo uploaded');
      reload();
    });
    fr.readAsDataURL(file);
  }

  const removeLogo = async () => {
    if (!await ask({ title: 'Remove the logo?', body: 'Documents and emails will go out without a logo until you upload a new one.', confirmLabel: 'Remove logo' })) return;
    run(async () => { await api.del('/brand/logo'); toast('Logo removed'); reload(); });
  };

  const mode = emailLog.status.mode;

  return (
    <>
      <PageHeader
        title="Settings"
        sub="Company, branding & crew"
        action={<button className="act" onClick={save} disabled={busy}>Save</button>}
      />
      <div className="view">
        <div className="split">
          <Card>
            <SectionTitle sub="Appears on proposals, agreements, invoices and outgoing email.">
              Brand logo
            </SectionTitle>
            <div style={{
              marginTop: 'var(--s-3)', background: 'var(--bg-subtle)',
              border: '1px dashed var(--border)', borderRadius: 'var(--r-4)',
              padding: 'var(--s-3)', minHeight: 84,
              display: 'grid', placeItems: 'center',
            }}>
              {f.logoDataUrl
                ? <img src={f.logoDataUrl} alt="Company logo" style={{ maxHeight: 60 }} />
                : <span className="sb">No logo uploaded yet</span>}
            </div>
            <div className="row" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              <button className="btn sm" onClick={() => logoRef.current?.click()}>
                {f.logoDataUrl ? 'Replace logo' : 'Upload logo'}
              </button>
              <input ref={logoRef} type="file" style={{ display: 'none' }}
                accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={onLogo} />
              {f.logoDataUrl ? <button className="btn sm dng" onClick={removeLogo}>Remove</button> : null}
            </div>
            <p className="sb" style={{ marginTop: 'var(--s-2)' }}>PNG, JPG, SVG, GIF or WEBP · max {MAX_LOGO_KB}KB</p>
          </Card>

          <Card>
            <div className="row between">
              <SectionTitle sub={
                mode === 'demo'
                  ? 'Demo mode — mail is captured below instead of sent. Set BREVO_API_KEY to send for real.'
                  : `Sending live via ${mode} from ${emailLog.status.from}`
              }>
                Email
              </SectionTitle>
              {emailLog.entries.length ? <Link to="/settings/emails" className="sb">View all →</Link> : null}
            </div>
            <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {emailLog.entries.slice(0, 6).map(e => (
                <Link className="card tight" key={e.id} to="/settings/emails" style={{ display: 'block' }}>
                  <div className="row between">
                    <span className="nm" style={{ fontSize: 'var(--fs-sm)' }}>{e.subject || '(no subject)'}</span>
                    <span className="sb">{fmtDateTime(e.at)}</span>
                  </div>
                  <div className="sb">
                    To {e.to} ·{' '}
                    <span style={{ color: e.delivered ? 'var(--green-700)' : 'var(--red-600)', fontWeight: 700 }}>
                      {e.delivered ? 'delivered' : 'failed'}
                    </span>
                  </div>
                </Link>
              ))}
              {!emailLog.entries.length ? <Empty>No emails sent yet.</Empty> : null}
            </div>
          </Card>
        </div>

        <CrewSection />

        <Card>
          <SectionTitle>Company</SectionTitle>
          <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            <Field label="Name" id="cn"><input id="cn" className="fi" value={f.companyName || ''} onChange={set('companyName')} /></Field>
            <div className="row-fields">
              <Field label="Owner" id="own"><input id="own" className="fi" value={f.ownerName || ''} onChange={set('ownerName')} /></Field>
              <Field label="Phone" id="ph"><input id="ph" className="fi" value={f.phone || ''} onChange={set('phone')} /></Field>
            </div>
            <div className="row-fields">
              <Field label="Email" id="em"><input id="em" className="fi" type="email" value={f.email || ''} onChange={set('email')} /></Field>
              <Field label="Address" id="ad"><input id="ad" className="fi" value={f.address || ''} onChange={set('address')} /></Field>
            </div>
          </div>
        </Card>

        <Card>
          <SectionTitle>Defaults</SectionTitle>
          <div className="row-fields" style={{ '--cols': 3, marginTop: 'var(--s-3)' }}>
            <Field label="Warranty (years)" id="wy"><input id="wy" className="fi" type="number" value={f.defaultWarrantyYears ?? ''} onChange={setNum('defaultWarrantyYears')} /></Field>
            <Field label="Deposit (%)" id="dp"><input id="dp" className="fi" type="number" value={f.defaultDepositPct ?? ''} onChange={setNum('defaultDepositPct')} /></Field>
            <Field label="Validity (days)" id="vd"><input id="vd" className="fi" type="number" value={f.defaultValidityDays ?? ''} onChange={setNum('defaultValidityDays')} /></Field>
          </div>
        </Card>

        {[
          ['Default terms', 'defaultTerms', 6, 'Attached to every new agreement.'],
          ['Default scope notes', 'defaultScopeNotes', 5, "Starting template for a new site visit's scope notes."],
          ['Default operational notes', 'defaultOperationalNotes', 6, 'Shown on every proposal between the scope and the total.'],
          ['Default warranty text', 'defaultWarrantyText', 3, 'Common wording used on every proposal.'],
          ['Default pricing notes', 'defaultPricingNotes', 4, null],
        ].map(([label, key, rows, sub]) => (
          <Card key={key}>
            <SectionTitle sub={sub}>{label}</SectionTitle>
            <textarea className="fi" rows={rows} style={{ marginTop: 'var(--s-3)' }} aria-label={label}
              value={f[key] || ''} onChange={set(key)} />
          </Card>
        ))}

        <ChangePasswordSection />

        <button className="btn pri" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
        <button className="btn" onClick={signOut}>Sign out</button>
      </div>
      {confirmDialog}
    </>
  );
}
